// authService.js - JWT Token Management Service

// In-memory token storage (not accessible via XSS)
let accessToken = null;
let tokenExpiresAt = null;

// --- Access Token Management ---

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
  accessToken = token;
  if (token) {
    try {
      // Decode JWT to get expiration (without verification - that's backend's job)
      const payload = JSON.parse(atob(token.split('.')[1]));
      tokenExpiresAt = payload.exp * 1000; // Convert to milliseconds
    } catch {
      tokenExpiresAt = Date.now() + 15 * 60 * 1000; // Fallback: 15 min
    }
  } else {
    tokenExpiresAt = null;
  }
};

export const clearAccessToken = () => {
  accessToken = null;
  tokenExpiresAt = null;
};

export const isTokenExpired = () => {
  if (!accessToken || !tokenExpiresAt) return true;
  // Consider expired 30 seconds before actual expiry (buffer for network latency)
  return Date.now() >= tokenExpiresAt - 30000;
};

// --- Refresh Token Management (localStorage fallback) ---

const REFRESH_TOKEN_KEY = 'refreshToken';

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

export const clearRefreshToken = () => localStorage.removeItem(REFRESH_TOKEN_KEY);

// --- Session Persistence (non-sensitive user data for UI) ---

export const persistSession = (backendUser, extra = {}) => {
  const user = {
    id: backendUser.id,
    email: backendUser.email,
    name: backendUser.name,
    role: backendUser.role,
    picture: backendUser.picture || extra.picture || null,
    wallet_address: backendUser.wallet_address || null,
    hasPassword: backendUser.hasPassword,
    hasGoogle: backendUser.hasGoogle,
    authMethod: extra.authMethod || 'password',
  };
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('userRole', backendUser.role);
  localStorage.setItem('userId', String(backendUser.id));
};

export const clearSession = () => {
  clearAccessToken();
  clearRefreshToken();
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userId');
};

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

export const isAuthenticated = () => {
  return !!accessToken && !isTokenExpired();
};

export const hasRefreshCapability = () => {
  // Check if we have a refresh token in localStorage
  return !!getRefreshToken();
};
